import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card } from "../components/ui";
import { RichText } from "../components/RichText";
import { SectionEditor } from "../components/SectionEditor";
import {
  amendmentDiffCopyButtonLabel,
  amendmentDiffCopyInProgress,
  amendmentDiffPreviewLabel,
  amendmentDiffToolView,
  emptyAmendmentDiffCopyState,
  nextAmendmentDiffCopyState,
  type AmendmentDiffCopyState,
} from "../domain/markupDoc";
import { emptyDoc, type RichTextDoc } from "../domain/richText";

export function AmendmentDiffTool({ publicAccess = false }: { publicAccess?: boolean }) {
  const [previous, setPrevious] = useState<RichTextDoc>(() => emptyDoc());
  const [amended, setAmended] = useState<RichTextDoc>(() => emptyDoc());
  const [copyState, setCopyState] = useState<AmendmentDiffCopyState>(() => emptyAmendmentDiffCopyState());
  const diffView = useMemo(() => amendmentDiffToolView({ previous, amended }), [amended, previous]);
  const copyDisabled = !diffView.canCopy || amendmentDiffCopyInProgress(copyState);

  const copyMarkup = async () => {
    if (copyDisabled) return;
    setCopyState((state) => nextAmendmentDiffCopyState(state, { type: "copyStarted" }));
    try {
      await navigator.clipboard.writeText(diffView.serializedDoc);
      setCopyState((state) => nextAmendmentDiffCopyState(state, { type: "copySucceeded" }));
      window.setTimeout(() => setCopyState((state) => nextAmendmentDiffCopyState(state, { type: "reset" })), 1800);
    } catch {
      setCopyState((state) => nextAmendmentDiffCopyState(state, { type: "copyFailed" }));
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] space-y-5 px-6 py-7 lg:px-9">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {publicAccess ? (
            <div className="text-sm font-semibold text-brand">Lawstream</div>
          ) : (
            <Link className="text-sm text-brand hover:underline" to="/editor">← Editor</Link>
          )}
          <h1 className="mt-1 font-serif text-2xl font-semibold text-slate-900">Amendment diff tool</h1>
        </div>
        <div className="flex items-center gap-3">
          <Legend />
          <Button icon="doc" variant="outline" disabled={copyDisabled} onClick={copyMarkup}>
            {amendmentDiffCopyButtonLabel(copyState)}
          </Button>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card className="p-4">
          <h2 className="mb-3 text-[14px] font-semibold text-slate-900">Previous provision</h2>
          <SectionEditor
            value={previous}
            onChange={setPrevious}
            placeholder="Paste or edit the previous provision text."
          />
        </Card>
        <Card className="p-4">
          <h2 className="mb-3 text-[14px] font-semibold text-slate-900">Amended provision</h2>
          <SectionEditor
            value={amended}
            onChange={setAmended}
            placeholder="Paste or edit the amended provision text."
          />
        </Card>
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-[14px] font-semibold text-slate-900">Update markup preview</h2>
          <span className="text-[12.5px] text-slate-500">
            {amendmentDiffPreviewLabel(diffView.previewStatus)}
          </span>
        </div>
        <div className="min-h-40 rounded border border-slate-200 bg-white px-4 py-3">
          {diffView.hasInput ? (
            <div className="ls-legal text-[15px]"><RichText doc={diffView.doc} legal /></div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-400">No amendment markup yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
}

function Legend() {
  return (
    <div className="hidden items-center gap-3 rounded-lg bg-white px-3 py-2 text-[12.5px] text-slate-600 ring-1 ring-slate-200 sm:flex">
      <span className="inline-flex items-center gap-1.5"><span className="ls-key-ins" /> Additions</span>
      <span className="inline-flex items-center gap-1.5"><span className="ls-key-del" /> Removals</span>
    </div>
  );
}
